import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql, Script } from 'gatsby';
import { Container, Row, Column, List, Icon } from '@pella/sash-framework';
import { SEO, Breadcrumbs, Image, Video, CTA } from '../../components';
import { useRouteChange } from '../../hooks/use-route-change';

import ImageBanner from '../../components/common/image-banner';
import CareersLayout from '../../components/layout/careers';
import CardSlider from '../../components/common/card-slider';
import ImageGrid from '../../components/common/image-grid';
import Feature from '../../components/common/feature';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';

import styles from './index.module.scss';

const Careers = (props) => {
	const [showMenu, setShowMenu] = useState(false);
	const pageData = props.data.contentstackCareers;
	const pageClass = pageData.url.replace(/\//g, '');

	useRouteChange({
		pageTitle: pageData.seo?.title,
		pageType: '',
	});

	return (
		<CareersLayout
			id='template-Careers'
			className={pageClass}
			activePage={props.location}
		>
			{pageData.breadcrumb && (
				<Breadcrumbs
					data={pageData.breadcrumb}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.new_hero && <HeroSimple data={pageData.new_hero} />}

			{pageData.anchor_links?.ctas?.[0] && (
				<div className={styles.anchorLinks}>
					{pageData.anchor_links.heading && <h2 className='s2-h2__alt'>{pageData.anchor_links.heading}</h2>}

					{pageData.anchor_links.ctas.length > 1 && (
						<button
							onClick={() => setShowMenu(!showMenu)}
							className={styles.anchorLinks_MobileButton}
						>
							In This Section
							<Icon name='arrow_drop_down' />
						</button>
					)}

					<List
						bullets='none'
						className={`${styles.anchorLinks_List} ${pageData.anchor_links.ctas.length > 1 ? styles.anchorLinks_List_multiple : ''} ${showMenu ? styles.anchorLinks_List_active : ''}`}
					>
						{pageData.anchor_links.ctas.map((link, index) => (
							<li key={index}>
								<CTA
									data={link}
									className={pageData.url === link.link?.href ? styles.careersAnchor_disabled : ''}
									disabled={pageData.url === link.link?.href}
								/>
							</li>
						))}
					</List>
				</div>
			)}

			{pageData.top_content && (
				<div className={styles.careersIntro}>
					<Container width={1800}>
						{pageData.top_content.heading && <h2 className='s2-h2__alt'>{pageData.top_content.heading}</h2>}
						{pageData.top_content.subheading && <p className={styles.careersIntro_Subheading}>{pageData.top_content.subheading}</p>}

						{pageData.top_content.logos_and_images?.[0] &&
							pageData.top_content.logos_and_images.map((image, index) => (
								<Image
									key={index}
									data={image}
								/>
							))}

						{pageData.top_content.columns?.[0] && (
							<Row
								className={styles.careersIntro_Row}
								verticalGutters={true}
							>
								{pageData.top_content.columns.map((col, index) => (
									<Column
										xs={12}
										md={col.column_size}
										key={index}
									>
										{col.heading && <p>{col.heading}</p>}

										{col.images?.[0] && (
											<List bullets='none'>
												{col.images.map((img, index) => (
													<li key={index}>
														<Image data={img} />
													</li>
												))}
											</List>
										)}

										{col.ctas?.[0] && <CTA data={col.ctas[0]} />}
									</Column>
								))}
							</Row>
						)}
					</Container>
				</div>
			)}

			{pageData.modular_blocks?.[0] &&
				pageData.modular_blocks.map((block, index) => {
					const setBgColor = (color) => (color ? styles[`careers_${color.replace(' ', '')}`] : '');

					if (block.generic != null) {
						return (
							<Container
								key={index}
								fluid
							>
								<Row justify='center'>
									<div
										className={styles.careersGenericSubheading}
										dangerouslySetInnerHTML={{
											__html: block.generic.subheading,
										}}
									/>
									<div
										className={styles.careersGenericDesc}
										dangerouslySetInnerHTML={{
											__html: block.generic.description,
										}}
									/>
								</Row>
							</Container>
						);
					}

					if (block.banners_and_grids != null) {
						return (
							<div
								key={index}
								id={block.banners_and_grids.anchor_text || null}
								className={`${setBgColor(block.banners_and_grids.background_color) || ''}`}
							>
								{block.banners_and_grids.grid?.[0] && (
									<Container width={1800}>
										<ImageGrid
											data={block.banners_and_grids.grid[0]}
											className={`${styles.careersImageGrid} ${block.banners_and_grids.background_color === 'gray dark' ? styles.careersImageGrid_dark : ''}`}
										/>
									</Container>
								)}
							</div>
						);
					}

					if (block.image_aside != null && block.image_aside.callout) {
						return (
							<ImageBanner
								data={block.image_aside}
								key={index}
								className='my-6 md:my-8 xl:my-12'
							/>
						);
					}

					if (block.image_aside != null && !block.image_aside.callout) {
						return (
							<Feature
								data={block.image_aside}
								key={index}
							/>
						);
					}

					if (block.video != null) {
						return (
							<div
								key={index}
								id={block.video.anchor_text || null}
								className={`${styles.careersVideo} ${setBgColor(block.video.background_color) || ''}`}
							>
								<Container width={1800}>
									{block.video.group?.[0] && (
										<Row
											justify='center'
											verticalGutters={true}
											largeGutters={true}
										>
											{block.video.group.map((vid, index) => {
												return (
													<Column
														key={index}
														xs={12}
														md={block.video.number_wide > 1 ? 12 / block.video.number_wide : 8}
													>
														<Video
															data={vid.video[0]}
															className={styles.careersVideo_Video}
														/>

														{vid.heading && <h3 className='s2-h3__alt'>{vid.heading}</h3>}
														{vid.subheading && (
															<div
																className={styles.careersVideo_Description}
																dangerouslySetInnerHTML={{
																	__html: vid.subheading,
																}}
															/>
														)}

														{vid.ctas?.[0] && <CTA data={vid.ctas[0]} />}
													</Column>
												);
											})}
										</Row>
									)}

									{block.video.ctas?.[0] && (
										<div className={styles.careersVideo_CTA}>
											<CTA data={block.video.ctas[0]} />
										</div>
									)}
								</Container>
							</div>
						);
					}

					if (block.image_group != null) {
						return (
							<div key={index}>
								{block.image_group.group?.[0] && (
									<CardSlider
										data={{
											items: block.image_group.group,
											carousel_background: block.image_group.background_color,
										}}
									/>
								)}
							</div>
						);
					}

					if (block.cta_ribbon?.ctas?.[0]) {
						return (
							<div
								key={index}
								className={styles.careersCtaRibbon}
							>
								<Container fluid>
									<div className={styles.careersAnchors}>
										{block.cta_ribbon.ctas.map((link, index) => (
											<CTA
												key={index}
												data={link}
											/>
										))}
									</div>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}
				})}

			{pageData.disclaimer && (
				<div className={styles.careersDisclaimers}>
					<Container width={1800}>
						<div
							dangerouslySetInnerHTML={{
								__html: pageData.disclaimer,
							}}
						/>
					</Container>
				</div>
			)}

			{pageData.link_group?.[0] && (
				<div className={`${styles.careersLinkGroup} ${pageData.link_group.length === 1 && styles.careersLinkGroup_Single}`}>
					<Container width={1800}>
						<Row
							align={pageData.link_group.length > 1 ? '' : 'center'}
							verticalGutters={true}
						>
							{pageData.link_group.length === 1 && (
								<>
									<Column
										xs={12}
										md='fluid'
									>
										{pageData.link_group[0].heading && <h3 className='s2-h3__alt'>{pageData.link_group[0].heading}</h3>}
										{pageData.link_group[0].subheading && <p>{pageData.link_group[0].subheading}</p>}
									</Column>

									<Column
										xs={12}
										md='auto'
									>
										{pageData.link_group[0].ctas?.[0] && <CTA data={pageData.link_group[0].ctas[0]} />}
									</Column>
								</>
							)}

							{pageData.link_group.length > 1 &&
								pageData.link_group.map((group, index) => (
									<Column
										key={index}
										xs={12}
										md='fluid'
									>
										{group.heading && <h3>{group.heading}</h3>}
										{group.subheading && <p>{group.subheading}</p>}

										{group.ctas?.[0] && <CTA data={group.ctas[0]} />}
									</Column>
								))}
						</Row>
					</Container>
				</div>
			)}

			{pageData.url === '/about/careers/' && (
				<Script
					id='roster-tracking'
					src='https://sa.getroster.com/sa.js?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhY2Nlc3NUb1VzZXJJZCI6IjExNDEyMTUiLCJzZXNzaW9uVXNlcklkIjoiMTE0MTIxNSIsImxvZ2luVHlwZUlkIjoiMCIsInJpZ2h0SWQiOiIxNjQ5IiwiYWNjZXNzSWQiOiI2IiwibmJmIjoxNzIxMzM0NjAxLCJleHAiOjE3MjEzMzgyMDEsImlhdCI6MTcyMTMzNDYwMX0.jWlp8f36XdnQ1KxBFe-Udzv1I9wJNJq-VDibePg7Bok'
				/>
			)}
		</CareersLayout>
	);
};

Careers.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Careers;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackCareers.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackCareers(id: { eq: $uid }) {
			url
			breadcrumb {
				active_page
				back_pages {
					...CTA
				}
			}
			new_hero {
				image {
					...Image
					image {
						gatsbyImageData(width: 1500, quality: 90)
					}
				}
				body
				cta {
					...CTA
				}
			}
			anchor_links {
				heading
				ctas {
					...CTA
				}
			}
			top_content {
				heading
				subheading
				columns {
					column_size
					heading
					text_position
					images {
						...Image
						image {
							gatsbyImageData(quality: 90)
						}
					}
					ctas {
						...CTA
					}
				}
			}
			modular_blocks {
				generic {
					heading
					subheading
					description
				}
				banners_and_grids {
					anchor_text
					background_color
					grid {
						...ImageGrid
					}
				}
				video {
					anchor_text
					background_color
					number_wide
					video_group
					group {
						heading
						subheading
						video {
							...Video
						}
						ctas {
							...CTA
						}
					}
				}
				image_group {
					background_color
					number_wide
					group {
						heading
						description
						image {
							...Image
							image {
								gatsbyImageData(quality: 90)
							}
						}
						ctas {
							...CTA
						}
					}
					ctas {
						...CTA
					}
				}
				cta_ribbon {
					heading
					ctas {
						...CTA
					}
				}
				cards {
					...CardSection
				}
				image_aside {
					...ImageAside
				}
			}
			disclaimer
			link_group {
				heading
				subheading
				ctas {
					...CTA
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
